import { LoadingBar } from 'quasar';
import { useAuth } from '~/composables/useAuth';

function resolveHeaders(config, auth) {
  const headers = { ...(config?.headers || {}) }
  if (!headers.Accept) {
    headers.Accept = 'application/json'
  }
  if (auth?.getAccessToken()) {
    headers['Authorization'] = `Bearer ${auth.getAccessToken()}`;
  }
  return headers
}

// Helper for interceptors
function createInterceptors() {
  const request = []
  const response = []

  return {
    request: {
      use(onFulfilled, onRejected) {
        request.push({ onFulfilled, onRejected })
      },
      list: request
    },
    response: {
      use(onFulfilled, onRejected) {
        response.push({ onFulfilled, onRejected })
      },
      list: response
    }
  }
}

function createHttp($fetch, auth) {
  const interceptors = createInterceptors();
  const http = {};
  let activeRequests = 0;

  function startLoading() {
    if (true && activeRequests === 0) {
      LoadingBar.start();
    }
    activeRequests += 1;
  }

  function stopLoading() {
    activeRequests = Math.max(0, activeRequests - 1);
    if (true && activeRequests === 0) {
      LoadingBar.stop();
    }
  }

  // Core request logic, with interceptors
  async function doRequest(rawConfig) {
    let config = { ...rawConfig }
    const useLoadingBar = config.loadingBar !== false;
    // --- Apply request interceptors
    for (const { onFulfilled, onRejected } of interceptors.request.list) {
      try {
        config = await onFulfilled(config)
      } catch (err) {
        if (onRejected) config = await onRejected(err)
        else throw err
      }
    }

    // ---- Perform $fetch
    let response, fetchError
    const enableHttpLog = false;
    try {
      if (useLoadingBar) startLoading();
      await auth.ensureFreshToken();
      if (enableHttpLog)
        console.log('plugin:http:perform:config', config);

      // Handle saves from vue-api-query by normalizing the body
      const isBodyInit =
        config.data instanceof FormData ||
        config.data instanceof URLSearchParams ||
        config.data instanceof Blob;

      const normalizedData =
        config.data && typeof config.data === "object" && !isBodyInit
          ? JSON.parse(JSON.stringify(config.data))
          : config.data;

      response = {
        data: await $fetch(config.url, {
          method: config.method || 'get',
          body: normalizedData,
          params: config.params,
          headers: resolveHeaders(config, auth),
          ...config,
        })
      };
      if (enableHttpLog)
        console.log('plugin:http:responseWithData', response)
    } catch (err) {
      fetchError = err
      if (enableHttpLog)
        console.log('plugin:http:errorRaw', err)
    } finally {
      if (useLoadingBar) stopLoading();
    }

    // --- Apply response interceptors
    let finalResult = response
    let errorObj = fetchError
    for (const { onFulfilled, onRejected } of interceptors.response.list) {
      try {
        if (errorObj && onRejected) {
          finalResult = await onRejected(errorObj)
          errorObj = undefined
        } else if (!errorObj && onFulfilled) {
          finalResult = await onFulfilled(finalResult)
        }
      } catch (err) {
        errorObj = err
      }
    }

    if (errorObj) throw errorObj
    return finalResult
  }

  http.request = (config) => doRequest(typeof config === 'string' ? { url: config } : config)

  // Shorthand methods
  for (const method of ['get', 'delete', 'head', 'options']) {
    http[method] = (url, config = {}) => doRequest({ ...config, url, method })
  }
  for (const method of ['post', 'put', 'patch']) {
    http[method] = (url, data, config = {}) => doRequest({ ...config, url, method, data })
  }
  // FormData variants
  function makeFormData(data) {
    if (data instanceof FormData) return data
    const fd = new FormData()
    for (const key in data) fd.append(key, data[key])
    return fd
  }
  for (const method of ['post', 'put', 'patch']) {
    http[method + 'Form'] = (url, data, config = {}) =>
      doRequest({ ...config, url, method, data: makeFormData(data) })
  }

  http.interceptors = interceptors

  return http
}

export default defineNuxtPlugin({
  name: 'http',
  dependsOn: ['pinia'],
  setup() {
    // Get the latest auth store for each request (client/server safe)
    const auth = useAuth();
    const http = createHttp($fetch, auth);

    // Provide for global access
    return {
      provide: {
        http
      }
    }
  }
})
